import groupBy from 'lodash/groupBy';
import reduce from 'lodash/reduce';

/**
 * hasParentWithClassName searches class name on the target or its parent elements
 * @param {Node} target - element whose parent might contain given class.
 * @param {String} className - class name string to be found
 */
export const hasParentWithClassName = (target, className) => {
  if (!target || !className) {
    return false;
  }

  let node = target;
  while (node) {
    if (node.classList && node.classList.contains(className)) {
      return true;
    }
    node = node.parentElement;
  }

  return false;
};

export const MOBILE_BREAKPOINT = 768;

export const isMobileViewport = () =>
  typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;

/**
 * Shift an info-card marker so the full card stays inside the map viewport.
 * On mobile, centers the card in the map viewport instead of anchoring to the pin.
 * Resets offset first, then measures actual DOM positions.
 */
export const adjustInfoCardVisibility = (map, marker, markerContainer, margin = 16) => {
  if (!map || !marker || !markerContainer) {
    return;
  }

  marker.setOffset([0, 0]);

  const mapRect = map.getContainer().getBoundingClientRect();
  const cardRect = markerContainer.getBoundingClientRect();

  let offsetX = 0;
  let offsetY = 0;

  if (isMobileViewport()) {
    const mapCenterX = mapRect.left + mapRect.width / 2;
    const mapCenterY = mapRect.top + mapRect.height / 2;
    const cardCenterX = cardRect.left + cardRect.width / 2;
    const cardCenterY = cardRect.top + cardRect.height / 2;

    offsetX = mapCenterX - cardCenterX;
    offsetY = mapCenterY - cardCenterY;
  } else {
    const overflowTop = margin - (cardRect.top - mapRect.top);
    const overflowBottom = cardRect.bottom - mapRect.bottom + margin;
    const overflowLeft = margin - (cardRect.left - mapRect.left);
    const overflowRight = cardRect.right - mapRect.right + margin;

    if (overflowTop > 0) {
      offsetY += overflowTop;
    }
    if (overflowBottom > 0) {
      offsetY -= overflowBottom;
    }
    if (overflowLeft > 0) {
      offsetX += overflowLeft;
    }
    if (overflowRight > 0) {
      offsetX -= overflowRight;
    }
  }

  if (offsetX !== 0 || offsetY !== 0) {
    marker.setOffset([offsetX, offsetY]);
  }

  // If centering on mobile pushed the card past an edge, nudge it back inside.
  if (isMobileViewport()) {
    const centeredRect = markerContainer.getBoundingClientRect();
    let correctionX = 0;
    let correctionY = 0;

    const overflowTop = margin - (centeredRect.top - mapRect.top);
    const overflowBottom = centeredRect.bottom - mapRect.bottom + margin;
    const overflowLeft = margin - (centeredRect.left - mapRect.left);
    const overflowRight = centeredRect.right - mapRect.right + margin;

    if (overflowTop > 0) {
      correctionY += overflowTop;
    }
    if (overflowBottom > 0) {
      correctionY -= overflowBottom;
    }
    if (overflowLeft > 0) {
      correctionX += overflowLeft;
    }
    if (overflowRight > 0) {
      correctionX -= overflowRight;
    }

    if (correctionX !== 0 || correctionY !== 0) {
      marker.setOffset([offsetX + correctionX, offsetY + correctionY]);
    }
  }
};

/**
 * Listings array grouped by geolocation
 * @param {Array} mapListings - listings to be grouped on map
 * @return {Object} - Object where coordinate pair is the key to different listings
 */
export const groupedByCoordinates = mapListings => {
  return groupBy(mapListings, l => {
    const g = l.attributes.geolocation;
    return `${g.lat}-${g.lng}`;
  });
};

/**
 * Listings (in location based object literal) is mapped to array
 * @param {Object} mapListings - listings to be grouped on map
 * @return {Array} - An array where items are arrays of listings
 *   (They are arrays containing all the listings in that location)
 */
export const reducedToArray = mapListings => {
  return reduce(mapListings, (acc, listing) => acc.concat([listing]), []);
};
